/**
 * The record page's words and the choices behind its dot line, kept apart
 * from the components so one test can gate every string (no em dashes, no
 * participial tails, CLAUDE.md) and the choices can be tested without a
 * render. The api decides what each turn did (`RecordTurn.kind`); this
 * file only says it in words and picks what the page opens on.
 */

import type {
  FailureRecordResponse,
  FailureRecordResponseFailureType,
  FailureRecordResponseSplit,
  RecordGeneration,
  RecordRefusal,
  RecordRun,
  RecordTurn,
  RecordTurnKind,
} from '@/api/generated/model'

/** A path with the search parameters that have a value. */
function withParams(path: string, params: Record<string, string | null>): string {
  const present = Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== null)
  return present.length === 0 ? path : `${path}?${new URLSearchParams(present).toString()}`
}

/**
 * The record page of a failure record, carrying the benchmark the refinery
 * showed so the page links back to it. A verdict's link names its round,
 * since a test run twice at one revision has one record id in both rounds.
 * A task's link names none and opens the copy generation planned.
 */
export function recordPath(recordId: string, benchmark: string | null, round: string | null): string {
  return withParams(`/refinery/records/${encodeURIComponent(recordId)}`, { benchmark, round })
}

/** The refinery on the benchmark a record page was opened from; with none it shows the team's default. */
export function refineryPath(benchmark: string | null): string {
  return withParams('/refinery', { benchmark })
}

/** "1 run" or "3 runs". */
function counted(count: number, noun: string): string {
  return `${count.toString()} ${noun}${count === 1 ? '' : 's'}`
}

/** A taxonomy tag in words: `reasoning_issue` reads "Reasoning issue". */
export function tagLabel(tag: string): string {
  const words = tag.split('_').join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A round's stamp as a day: `20261012T050000Z` reads "Oct 12, 2026". A name
 * with no stamp is shown as it is. */
export function roundLabel(round: string): string {
  const match = /(\d{4})(\d{2})(\d{2})T\d{6}Z$/.exec(round)
  const [year, month, day] = [match?.[1], match?.[2], match?.[3]]
  if (year === undefined || month === undefined || day === undefined) return round
  return new Date(`${year}-${month}-${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function failedRuns(record: FailureRecordResponse): number {
  return record.runs_analysed - record.runs_passed
}

/** "6 of 8 runs failed on acme/dispatch-v2 in the round of Oct 12, 2026." */
export function runsSentence(record: FailureRecordResponse): string {
  return `${failedRuns(record).toString()} of ${counted(record.runs_analysed, 'run')} failed on ${record.model} in the round of ${roundLabel(record.round)}.`
}

/** The analyst reads a capped number of failed runs, so its counts can stop
 * short of the rows drawn: the page says so. Null when it read them all. */
export function failedRunsReadSentence(record: FailureRecordResponse): string | null {
  const failed = failedRuns(record)
  if (record.failed_runs_read === failed) return null
  return `The analysis read ${record.failed_runs_read.toString()} of the ${failed.toString()} failed runs.`
}

/** Who wrote the analysis: the page says it is a model's, by name when known. */
export function analystSentence(record: FailureRecordResponse): string {
  const writer = record.analyst === '' ? 'A model' : record.analyst
  return `${writer} wrote the analysis on this page from the test's runs. The dots come from the transcripts.`
}

/** "The failing runs left the golden process at step 3 of 4: keep the ETA." */
export function stepSentence(record: FailureRecordResponse): string {
  const step = record.golden_steps[record.step_index - 1] ?? ''
  return `The failing runs left the golden process at step ${record.step_index.toString()} of ${record.golden_steps.length.toString()}: ${step}`
}

/** "Of the runs the analyst read, 6 reached this step and 2 passed after it." */
export function difficultySentence(record: FailureRecordResponse): string {
  return `Of the runs the analyst read, ${record.reached_failure_point.toString()} reached this step and ${record.passed_after_it.toString()} passed after it.`
}

export function eligibilitySentence(record: FailureRecordResponse): string {
  return record.eligible
    ? RECORD_COPY.eligible
    : `${RECORD_COPY.notEligible} ${record.eligible_reason}`
}

/** The test's file name, the last part of its path. */
export function fileName(path: string): string {
  return path.split('/').pop() ?? path
}

/** "Run 1, turn 2 of 3". */
export function turnPlace(run: RecordRun, turn: RecordTurn): string {
  const position = run.turns.indexOf(turn) + 1
  return `Run ${run.pass_index.toString()}, turn ${position.toString()} of ${run.turns.length.toString()}`
}

/** A turn's identity on the page. */
export function turnKey(run: RecordRun, turn: RecordTurn): string {
  return `${run.transcript}#${turn.index.toString()}`
}

/** The turn the dot line outlines; null with none. */
export function pivotOf(run: RecordRun): RecordTurn | null {
  return run.turns.find((turn) => turn.index === run.pivot) ?? null
}

/** The turns that call a tool, from their `name(arguments)` calls. */
function turnsCalling(record: FailureRecordResponse, tool: string): RecordTurn[] {
  return record.runs
    .flatMap((run) => run.turns)
    .filter((turn) => turn.calls.some((call) => call.split('(', 1)[0] === tool))
}

/** Words the analyst writes in a tool field for a step that took no tool.
 * Its tool fields are free text, and a real tool the runs skipped can read
 * the same as these until a run calls it, so the list has to name them. */
const NO_CALL_WORDS: ReadonlySet<string> = new Set(['final_response', 'finish', 'none'])

/** Whether a tool field holds the analyst's word for a reply with no call.
 * A name a run does call is a tool, whatever it reads like. */
export function namesNoCall(record: FailureRecordResponse, tool: string): boolean {
  return NO_CALL_WORDS.has(tool) && turnsCalling(record, tool).length === 0
}

/** How the legend marks a call the failing runs made: the way its dots are
 * marked. The expected tool is a filled dot unless the analyst named the turn,
 * and a name no turn calls has no dot of its own, so it takes a reply's ring. */
export function madeCallKind(record: FailureRecordResponse, tool: string): RecordTurnKind {
  const kinds = turnsCalling(record, tool).map((turn) => turn.kind)
  if (kinds.includes('wrong')) return 'wrong'
  return kinds.includes('expected') ? 'expected' : 'text'
}

/** What the record's marks mean, for the marks its dots carry. */
export function legendSentences(record: FailureRecordResponse): string[] {
  const diamonds = record.runs.some((run) => run.turns.some((turn) => turn.kind === 'wrong'))
  const outlined = record.runs.some((run) => pivotOf(run) !== null)
  const outline = record.failing_turns_named ? RECORD_COPY.legendOutlineNamed : RECORD_COPY.legendOutline
  return [...(diamonds ? [RECORD_COPY.legendDiamond] : []), ...(outlined ? [outline] : [])]
}

/** What the page opens on: the first failed run's outlined turn, else the
 * first turn of the first run with any; null with no turn at all. */
export function firstSelection(runs: RecordRun[]): string | null {
  for (const run of runs) {
    const pivot = pivotOf(run)
    if (pivot !== null) return turnKey(run, pivot)
  }
  for (const run of runs) {
    const [turn] = run.turns
    if (turn !== undefined) return turnKey(run, turn)
  }
  return null
}

/** Whether no turn of any run is marked wrong though the failure is a
 * wrong argument, which tool names cannot tell in a round from before the
 * analyst named each run's turn: the page says so. */
export function argumentUntold(record: FailureRecordResponse): boolean {
  return (
    !record.failing_turns_named &&
    record.failure_type === 'wrong_argument' &&
    record.runs.every((run) => pivotOf(run) === null)
  )
}

/** What followed the record in task generation, as sentences. */
export function followedSentences(generation: RecordGeneration): string[] {
  const copy = RECORD_COPY.followed
  switch (generation.state) {
    case 'generated':
      return generatedSentences(generation)
    case 'no_cards':
      return [copy.noCards]
    case 'regenerate':
      // The mark keeps the earlier start's outcome until the next start replaces it.
      return [
        ...(generation.batch === null ? [copy.noCards] : generatedSentences(generation)),
        copy.regenerate,
      ]
    case 'skipped':
      return [generation.refusal === null ? copy.skipped : copy.refusal[generation.refusal]]
    case 'waiting':
      return [copy.waiting]
    case 'unavailable':
      return [copy.unavailable]
  }
}

/** The batch a start made of the record: its cards, gate and datasets. */
function generatedSentences(generation: RecordGeneration): string[] {
  const planned =
    generation.accepted !== null && generation.cards !== null
      ? `The reviewer accepted ${generation.accepted.toString()} of ${counted(generation.cards, 'card')}.`
      : RECORD_COPY.followed.generated
  return [planned, ...gateSentences(generation)]
}

function gateSentences(generation: RecordGeneration): string[] {
  const copy = RECORD_COPY.followed
  const gate = generation.gate
  if (gate === null) return [copy.gateWaiting]
  if (gate.refused) return [copy.gateRefused]
  const proven = `The task gate proved ${gate.proven.toString()} of ${counted(gate.cases, 'task')}.`
  const trained =
    gate.datasets.length === 0 ? copy.noDataset : `Trained in ${gate.datasets.join(', ')}.`
  return [proven, trained]
}

export const TURN_KIND_LABEL: Record<RecordTurnKind, string> = {
  wrong: 'Wrong call',
  expected: 'Expected call',
  tool: 'Tool call',
  text: 'Text reply',
}

export const FAILURE_TYPE_LABEL: Record<FailureRecordResponseFailureType, string> = {
  wrong_tool: 'Wrong tool',
  wrong_argument: 'Wrong argument',
  missing_call: 'Missing call',
  stopped_early: 'Stopped early',
  wrong_order: 'Wrong order',
}

export const SPLIT_LABEL: Record<FailureRecordResponseSplit, string> = {
  train: 'Train',
  held_out: 'Held out',
  unassigned: 'Unassigned',
}

const REFUSAL: Record<RecordRefusal, string> = {
  other_benchmark: 'Task generation does not plan it: the record names another benchmark.',
  not_model: "Task generation does not plan it: the failure is the environment's.",
  runs_folder_id: "Task generation does not plan it: its id is the gate's runs folder.",
  no_deny_list: 'Task generation does not plan it: the record has no deny-list.',
}

/** Every fixed string the page renders, so one test can gate them all. */
export const RECORD_COPY = {
  crumb: 'Data refinery',
  newLabel: 'New label',
  loadFailed: 'Failed to load the record:',
  notFoundTitle: 'No record with this id',
  notFoundDescription: 'The failure analysis of this team holds no record by this id.',
  runsTitle: 'Runs',
  runsDescription: 'One row per run of the test. Click a dot to read that turn.',
  turnsLabel: 'Turns of each run',
  turnAxis: 'Turn',
  run: 'Run',
  failed: 'Failed',
  passed: 'Passed',
  expected: 'Expected',
  otherTool: 'Other tool call',
  textReply: 'Text reply',
  legendDiamond: 'A diamond is a call to a tool the analysis names as wrong.',
  legendOutline: "The outlined diamond is the run's first.",
  legendOutlineNamed: 'The outline marks the turn where the analysis says the run failed.',
  argumentUntold:
    'The failing runs called the expected tool with a wrong argument. Tool names cannot tell the two apart, so no turn is marked.',
  noRuns: 'The record has no transcript to draw.',
  firstWrong: 'first wrong call',
  failingTurn: 'failing turn',
  notRead: 'Not read',
  calls: 'Calls',
  result: 'Result',
  noResult: 'No tool answered this turn.',
  openTranscript: 'Open the whole transcript',
  closeTranscript: 'Hide the transcript',
  transcriptLoading: 'Loading the transcript',
  transcriptFailed: 'Failed to load the transcript:',
  transcriptTruncated: 'The transcript is longer than shown here.',
  details: 'Analysis details',
  detailsHint: 'Golden process, state at failure, confounds, difficulty and what task generation reads',
  goldenProcess: 'Golden process',
  failureTurn: 'Failure turn',
  stateAtFailure: 'State at failure',
  environmentFacts: 'Environment facts',
  confounds: 'Confounds',
  difficulty: 'Difficulty',
  gradingRule: 'Grading rule',
  variationAxes: 'Variation axes',
  eligibility: 'Eligibility',
  eligible: 'Eligible for task generation.',
  notEligible: 'Not eligible for task generation:',
  none: 'None',
  followedTitle: 'What followed',
  seeTasks: 'See the tasks in the refinery',
  followed: {
    generated: 'Task generation planned it into a batch.',
    noCards: 'Task generation planned it, and the reviewer accepted no card.',
    regenerate: 'Staff marked it to plan again at the next task generation run.',
    skipped: 'Task generation does not plan it.',
    waiting: 'The next task generation run plans it.',
    unavailable:
      "No task generation run plans it, since the team's evals config names no scored benchmark in a repository.",
    gateWaiting: 'The task gate has not judged the batch yet.',
    gateRefused: 'The task gate refused the batch.',
    noDataset: 'No dataset has trained on it yet.',
    refusal: REFUSAL,
  },
} as const
